import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  ChevronDown as CaretDownIcon,
  ChevronRight as CaretRightIcon,
  Cpu as CpuIcon,
  FileText as FileTextIcon,
  Layers3 as LayersIcon,
  Search as SearchIcon,
  Shield as ShieldIcon
} from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  filterSafetyLanding,
  filterSrsFunctionGroups,
  groupProtectiveFunctionsBySrsGroup,
  mergeSafetyLanding,
  useGetPlantFunctionLogicDiagramsQuery,
  useGetPlantProtectiveFunctionsQuery,
  type PlantFunctionLogicDiagramSummary,
  type PlantLogicSolver,
  type PlantSrsFunctionGroupIndex
} from '@axilon-apps/domains-ontology-ui-state'
import { Input } from '../../components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table'
import { Empty } from '../../components/primitives/Empty'
import {
  plantFunctionLogicDiagramHref,
  plantLogicSolverHref,
  plantProtectiveFunctionHref,
  plantSrsFunctionGroupHref
} from '../../lib/plant-links'
import { CollapsibleContent } from './CollapsibleContent'

/**
 * `/plant/safety` — Safety tab landing.
 *
 * Keeps SRS safety functions and Safety Manager implementation catalogs in
 * separate, lazily loaded views. Their cross-source `IMPLEMENTED_BY` links
 * remain visible on each side without implying that their solver identities
 * are the same node.
 */
const SafetyPage = (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const view: SafetyView = searchParams.get('tab') === 'safety-manager' ? 'safety-manager' : 'srs'
  const functionsQ = useGetPlantProtectiveFunctionsQuery(undefined, { skip: view !== 'srs' })
  const diagramsQ = useGetPlantFunctionLogicDiagramsQuery(undefined, {
    skip: view !== 'safety-manager'
  })
  const functions = React.useMemo(
    () => (view === 'srs' ? (functionsQ.data ?? []) : []),
    [functionsQ.data, view]
  )
  const diagrams = React.useMemo(
    () => (view === 'safety-manager' ? (diagramsQ.data ?? []) : []),
    [diagramsQ.data, view]
  )
  const [search, setSearch] = React.useState('')
  const srsGroups = React.useMemo(() => groupProtectiveFunctionsBySrsGroup(functions), [functions])
  const filteredSrsGroups = React.useMemo(
    () => filterSrsFunctionGroups(srsGroups, search),
    [srsGroups, search]
  )
  const merged = React.useMemo(() => mergeSafetyLanding([], diagrams), [diagrams])
  const index = React.useMemo(() => filterSafetyLanding(merged, search), [merged, search])
  const shown =
    view === 'srs'
      ? filteredSrsGroups.ungrouped.length +
        filteredSrsGroups.groups.reduce((sum, group) => sum + group.functions.length, 0)
      : index.unsolvedDiagrams.length +
        index.solvers.reduce((sum, group) => sum + group.diagrams.length, 0)
  const total = view === 'srs' ? functions.length : diagrams.length
  const hasGroups =
    view === 'srs'
      ? filteredSrsGroups.groups.length > 0 || filteredSrsGroups.ungrouped.length > 0
      : index.solvers.length > 0 || index.unsolvedDiagrams.length > 0
  const searchActive = search.trim().length > 0
  const activeQuery = view === 'srs' ? functionsQ : diagramsQ
  const isLoading = activeQuery.isLoading && !hasGroups
  const isError = activeQuery.isError
  const isEmpty = total === 0

  const changeView = (next: string) => {
    if (next !== 'srs' && next !== 'safety-manager') return
    const nextParams = new URLSearchParams(searchParams)
    if (next === 'srs') nextParams.delete('tab')
    else nextParams.set('tab', next)
    setSearchParams(nextParams, { replace: true })
    setSearch('')
  }

  return (
    <div className="flex flex-1 flex-col gap-card">
      <header>
        <h1 className="m-0 text-2xl font-semibold text-ink">Safety</h1>
        <p className="mt-1 text-sm text-ink-3">
          {viewSubtitle(
            view,
            total,
            shown,
            search,
            view === 'srs' ? filteredSrsGroups.groups.length : undefined
          )}
        </p>
      </header>

      <Tabs value={view} onValueChange={changeView}>
        <TabsList variant="line" aria-label="Safety source">
          <TabsTrigger value="srs">Safety functions (SRS)</TabsTrigger>
          <TabsTrigger value="safety-manager">Safety Manager</TabsTrigger>
        </TabsList>
      </Tabs>

      {isError ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title={`Couldn't load ${view === 'srs' ? 'SRS safety functions' : 'Safety Manager data'}`}
            hint="Check the proxy is reachable and try again."
            icon={WarningCircleIcon}
          />
        </div>
      ) : isLoading ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <div className="text-sm text-ink-3">Loading safety data…</div>
        </div>
      ) : isEmpty ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title={view === 'srs' ? 'No SRS safety functions yet' : 'No Safety Manager data yet'}
            hint={
              view === 'srs'
                ? 'Ingest a Safety Requirements Specification to add protective functions.'
                : 'Ingest a Safety Manager source to add logic solvers, FLDs, and safety points.'
            }
            icon={ShieldIcon}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-card">
          <div className="flex items-center gap-2">
            <SearchIcon size={14} className="text-ink-3" aria-hidden />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                view === 'srs'
                  ? 'Search by SIF, function group, SIL, unit, or solver…'
                  : 'Search by FLD, linked SIF, or solver…'
              }
              className="h-8 max-w-md text-xs"
            />
          </div>
          {!hasGroups ? (
            <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
              <Empty
                title={view === 'srs' ? 'No matching safety functions' : 'No matching FLDs'}
                hint="Try a different search."
                icon={SearchIcon}
              />
            </div>
          ) : view === 'srs' ? (
            <SrsSafetyLanding index={filteredSrsGroups} search={search} />
          ) : (
            <SafetyManagerLanding index={index} searchActive={searchActive} />
          )}
        </div>
      )}
    </div>
  )
}

type SafetyView = 'srs' | 'safety-manager'

function SrsSafetyLanding({
  index,
  search
}: {
  index: PlantSrsFunctionGroupIndex
  search: string
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-card">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {index.groups.map((group) => {
          const categories = Array.from(
            new Set(
              group.sections
                .map((section) => section.function_category?.trim())
                .filter((value): value is string => Boolean(value))
            )
          )
          return (
            <Link
              key={group.id}
              to={plantSrsFunctionGroupHref(group.id, search)}
              className="flex min-w-0 items-center gap-3 rounded-xl border bg-card p-4 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
                <LayersIcon size={17} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink">{group.name}</span>
                <span className="mt-0.5 block truncate text-xs text-ink-3">
                  {group.site?.name ? `${group.site.name} · ` : ''}
                  {countLabel(group.functions.length, 'SIF')} ·{' '}
                  {countLabel(categories.length, 'category')}
                </span>
                {categories.length > 0 ? (
                  <span className="mt-1 block truncate text-11 text-ink-3">
                    {categories.join(' · ')}
                  </span>
                ) : null}
              </span>
              <CaretRightIcon size={15} className="shrink-0 text-ink-4" aria-hidden />
            </Link>
          )
        })}
      </div>

      {index.ungrouped.length > 0 ? (
        <section className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <ShieldIcon size={15} className="text-ink-4" aria-hidden />
            <span className="font-semibold text-ink">Functions without an SRS group</span>
            <span className="ml-auto text-xs text-ink-3">
              {countLabel(index.ungrouped.length, 'SIF')}
            </span>
          </div>
          <div className="grid gap-px bg-border sm:grid-cols-2 xl:grid-cols-3">
            {index.ungrouped.map((fn) => (
              <Link
                key={fn.id}
                to={plantProtectiveFunctionHref(fn.id)}
                className="truncate bg-card px-4 py-2.5 text-sm font-medium text-ink no-underline hover:bg-accent hover:text-accent-foreground"
              >
                {fn.name}
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}

function SafetyManagerLanding({
  index,
  searchActive
}: {
  index: ReturnType<typeof filterSafetyLanding>
  searchActive: boolean
}): React.ReactElement {
  return (
    <>
      {index.solvers.map((group) => (
        <DiagramTableSection
          key={group.solver.id}
          solver={group.solver}
          diagrams={group.diagrams}
          forceOpen={searchActive}
        />
      ))}
      {index.unsolvedDiagrams.length > 0 && (
        <DiagramTableSection
          label="No Safety Manager logic solver"
          diagrams={index.unsolvedDiagrams}
          forceOpen={searchActive}
        />
      )}
    </>
  )
}

function DiagramTableSection({
  solver,
  label,
  diagrams,
  forceOpen = false
}: {
  solver?: PlantLogicSolver
  label?: string
  diagrams: PlantFunctionLogicDiagramSummary[]
  forceOpen?: boolean
}): React.ReactElement {
  const [open, setOpen] = React.useState(false)
  const expanded = forceOpen || open
  return (
    <div className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
      <button
        type="button"
        onClick={() => {
          if (forceOpen) return
          setOpen((value) => !value)
        }}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 p-3.5 text-left hover:bg-accent hover:text-accent-foreground"
      >
        {expanded ? (
          <CaretDownIcon size={14} className="shrink-0 text-ink-3" aria-hidden />
        ) : (
          <CaretRightIcon size={14} className="shrink-0 text-ink-3" aria-hidden />
        )}
        <CpuIcon size={16} className="shrink-0 text-ink-4" aria-hidden />
        <span className="min-w-0 flex-1">
          {solver ? (
            <Link
              to={plantLogicSolverHref(solver.id)}
              className="block truncate text-sm font-semibold text-ink no-underline hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {solver.name}
            </Link>
          ) : (
            <span className="block truncate text-sm font-semibold text-ink">{label}</span>
          )}
          {solverDescription(solver) ? (
            <span className="mt-0.5 block truncate text-xs text-ink-3">
              {solverDescription(solver)}
            </span>
          ) : null}
        </span>
        <span className="shrink-0 text-xs text-ink-3">{countLabel(diagrams.length, 'FLD')}</span>
      </button>
      <CollapsibleContent open={expanded}>
        <FldTable diagrams={diagrams} solver={solver} />
      </CollapsibleContent>
    </div>
  )
}

function FldTable({
  diagrams,
  solver
}: {
  diagrams: PlantFunctionLogicDiagramSummary[]
  solver?: PlantLogicSolver
}): React.ReactElement {
  return (
    <div className="border-t">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>FLD</TableHead>
            <TableHead>Linked SRS functions</TableHead>
            <TableHead className="w-[16%] text-right">Points</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {diagrams.slice(0, 25).map((diagram) => (
            <TableRow key={diagram.id}>
              <TableCell>
                <Link
                  to={plantFunctionLogicDiagramHref(diagram.id)}
                  className="inline-flex items-center gap-1 font-medium text-ink no-underline hover:underline"
                >
                  <FileTextIcon size={12} className="text-ink-4" aria-hidden />
                  {diagram.name}
                </Link>
              </TableCell>
              <TableCell>
                {diagram.implementedBy.length === 0 ? (
                  <span className="text-ink-4">—</span>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {diagram.implementedBy.slice(0, 3).map((fn) => (
                      <Link
                        key={fn.id}
                        to={plantProtectiveFunctionHref(fn.id)}
                        className="truncate text-ink no-underline hover:underline"
                      >
                        {fn.name}
                      </Link>
                    ))}
                    {diagram.implementedBy.length > 3 ? (
                      <span className="text-11 text-ink-3">
                        +{diagram.implementedBy.length - 3} more
                      </span>
                    ) : null}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">{diagram.pointCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {diagrams.length > 25 && (
        <div className="px-3.5 py-2 text-xs text-ink-3">
          Showing 25 of {diagrams.length}.
          {solver ? (
            <>
              {' '}
              <Link
                to={plantLogicSolverHref(solver.id)}
                className="font-medium text-ink hover:underline"
              >
                Open the solver to browse the rest.
              </Link>
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}

function solverDescription(solver?: PlantLogicSolver): string | undefined {
  if (!solver) return undefined
  return [solver.type, solver.vendor, solver.application].filter(Boolean).join(' · ') || undefined
}

function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function viewSubtitle(
  view: SafetyView,
  total: number,
  shown: number,
  search: string,
  groupCount?: number
): string {
  const searching = search.trim().length > 0
  const count = searching && shown !== total ? `${shown} of ${total}` : `${total}`
  return view === 'srs'
    ? `${groupCount ?? 0} function group${groupCount === 1 ? '' : 's'} · ${count} SRS protective function${total === 1 ? '' : 's'}`
    : `${count} Safety Manager FLD${total === 1 ? '' : 's'}`
}

export default SafetyPage
